import React, { useState } from "react";
import PaystackPop from "@paystack/inline-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CreditCard, Shield, Info } from "lucide-react";
import { paymentService, SubscriptionPlan } from "@/services/paymentService";
import { useToast } from "@/hooks/use-toast";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";

interface PaymentGatewaySelectionProps {
  plan: SubscriptionPlan;
  onPaymentInitiated: (sessionId: string, redirectUrl?: string) => void;
  isProcessing: boolean;
  setIsProcessing: (processing: boolean) => void;
  onCloseDialog?: () => void;
}

const PaymentGatewaySelection: React.FC<PaymentGatewaySelectionProps> = ({
  plan,
  onPaymentInitiated,
  isProcessing,
  setIsProcessing,
  onCloseDialog,
}) => {
  const { toast } = useToast();
  const { user } = useSupabaseAuth();

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      const session = await paymentService.createPaymentSession(
        plan,
        "paystack",
        user,
      );

      toast({
        title: "Processing payment...",
        description: "Initializing payment with Paystack",
      });

      // Prefer Paystack Popup (in-page modal) over full-page redirect
      if (session.accessCode) {
        // Close dialog so the Paystack modal isn't obscured
        if (onCloseDialog) {
          onCloseDialog();
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        onPaymentInitiated(session.sessionId);
        setIsProcessing(false); // Release button — Popup takes over

        const popup = new PaystackPop();
        popup.resumeTransaction(session.accessCode, {
          onSuccess: (transaction: { reference: string; trxref: string }) => {
            window.location.href = `/payment/success?provider=paystack&plan_id=${plan?.id}&reference=${transaction.reference}&trxref=${transaction.trxref}`;
          },
          onCancel: () => {
            toast({
              title: "Payment Cancelled",
              description:
                "You closed the payment window. Try again when ready.",
            });
          },
        });
      } else if (session.redirectUrl) {
        if (onCloseDialog) {
          onCloseDialog();
          await new Promise((resolve) => setTimeout(resolve, 200));
        }

        onPaymentInitiated(session.sessionId, session.redirectUrl);
        window.location.href = session.redirectUrl;
      } else {
        throw new Error(
          "Payment initialization failed: no access code or redirect URL",
        );
      }
    } catch (error) {
      console.error("Payment initiation failed:", error);
      toast({
        title: "Payment Failed",
        description: "Unable to initialize payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Info className="h-5 w-5" />
          Payment Summary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-muted/30 p-4 rounded-lg space-y-2 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-sm">Plan:</span>
            <span className="font-medium">{plan.name}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm">Billing cycle:</span>
            <span className="font-medium">Every {plan.interval}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm">Amount:</span>
            <span className="font-bold text-lg">
              {plan.currency === "ZAR" ? "R" : "$"}
              {plan.price}
            </span>
          </div>
          <Separator className="my-2" />
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <CreditCard className="h-3 w-3" />
              Payment processor:
            </span>
            <span className="flex items-center gap-1">
              {/* Light mode */}
              <img
                src="/paystack.png"
                alt="Paystack"
                className="h-6 object-contain dark:hidden"
              />

              {/* Dark mode */}
              <img
                src="/paystack_dark.png"
                alt="Paystack"
                className="h-6 object-contain hidden dark:block"
              />
            </span>
          </div>
        </div>

        <Button
          onClick={handlePayment}
          disabled={isProcessing}
          className="w-full hover:bg-teal-700 hover:text-white transition-colors transition-duration-300"
          size="lg"
        >
          {isProcessing ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Processing...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Continue to Payment
            </div>
          )}
        </Button>

        <Button
          variant="outline"
          onClick={onCloseDialog}
          disabled={isProcessing}
          className="w-full mt-3"
          size="lg"
        >
          Cancel
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-4 flex items-center justify-center gap-1">
          <Shield className="h-3 w-3" />
          Secured with bank-level encryption via Paystack
        </p>
      </CardContent>
    </Card>
  );
};

export default PaymentGatewaySelection;
